import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { gql, useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { Spinner } from 'admin-components/components';
import { bookingShape } from 'constants/propTypes';
import { ACTION_CODES } from 'constants/errors';
import { parsePhoneNumber } from 'utils/parsePhoneNumber';
import AgentPortalError from 'errors/AgentPortalError';
import { isInfantsOnLapEnabled } from 'utils/featureFlags';
import { ELIGIBLE_LAP_PASSENGER_TYPES } from 'constants/passengers';
import formatPassengers from 'utils/formatPassengers';
import { renameKeys } from 'utils/functions';
import { hasDog } from 'utils/booking';
import { GET_CUSTOMER_DETAILS } from 'store/queries';
import { ADD_CUSTOMER_PASSENGERS, ADD_INFANT, UPDATE_CUSTOMER_PASSENGERS, UPDATE_PASSENGERS } from 'store/mutations';
import usePayIfLoyaltyAndConfirmBookingMutation from 'components/mutations/usePayIfLoyaltyAndConfirmBookingMutation';
import { getInfantPassengersFromDetails } from 'utils/getInfantPassengersFromDetails';
import { hasTrainSegment } from '../BookingDetails/Extras/Extras.utils';
import { isFakeCustomer } from 'utils/isFakeCustomer';

import BookingPathPassengerDetails from './BookingPathPassengerDetails';
import { Column } from '../../components/shared/Layout';

const BookingPathPassengerDetailsQuery = ({ bookingData, canAddSpecialAssistanceToBooking, ...props }) => {
  const [isAssistanceSelected, setIsAssistanceSelected] = useState(hasDog(bookingData));
  const history = useHistory();
  const client = useApolloClient();

  const infantsOnLapEnabled = isInfantsOnLapEnabled();
  const rawInfantCount = Number(new URLSearchParams(history.location.search).get('infants')) || 0;
  const maxInfants = bookingData.passengers.filter((p) => ELIGIBLE_LAP_PASSENGER_TYPES.includes(p.type)).length;
  const infantCount = Math.min(Math.max(0, rawInfantCount), maxInfants);
  const [infantAddError, setInfantAddError] = useState(false);

  const [addInfant] = useMutation(ADD_INFANT);

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const subscriptionID = new URLSearchParams(history.location.search).get('subscriptionID');
  const subscriptionCode = new URLSearchParams(history.location.search).get('subscriptionCode');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';
  const subscriptionIDUrl = subscriptionID ? `&subscriptionID=${subscriptionID}` : '';
  const subscriptionCodeUrl = subscriptionCode ? `&subscriptionCode=${subscriptionCode}` : '';
  const { firstName, lastName, email, phoneNumber } = bookingData?.customer || {};
  const hasCustomerDetails = Boolean(firstName && lastName && email);
  const hasFakeCustomer = isFakeCustomer({ firstName, lastName, email, phoneNumber });

  const isExtrasOnlyBooking =
    !hasTrainSegment(bookingData) && Boolean(bookingData?.outboundExtras?.length || bookingData?.inboundExtras?.length);
  const shouldUseReviewBookingScreen = !isExtrasOnlyBooking;

  const determineOnCompletedRoute = ({ hasNothingDue }) => {
    if (isAssistanceSelected || history.location.state?.isPackageBooking) {
      return `/booking/${bookingData.reference}/pax/assistance${membershipNumberUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`;
    }

    if (shouldUseReviewBookingScreen) {
      return `/booking/${bookingData.reference}/review-booking${membershipNumberUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`;
    }

    if (hasNothingDue) {
      return `/manage/bookings/${bookingData.reference}${membershipNumberUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`;
    }

    return `/booking/${bookingData.reference}/payment${membershipNumberUrl}${subscriptionIDUrl}${subscriptionCodeUrl}`;
  };

  const [confirmBooking, { error: confirmBookingError }] = usePayIfLoyaltyAndConfirmBookingMutation();

  const [getCustomerInfo, { called: customerInfoCalled, loading: customerInfoLoading, data: customerData }] =
    useLazyQuery(GET_CUSTOMER_DETAILS);

  const customerInfo = customerData?.getCustomerDetails;

  const handleCompletedMutation = async (infants = []) => {
    const hasNothingDue = bookingData.totalPriceToBePaid === 0 && !shouldUseReviewBookingScreen;
    const pathname = determineOnCompletedRoute({ reference: bookingData.reference, hasNothingDue });

    if (infantsOnLapEnabled && infantCount > 0) {
      const existingInfants = getInfantPassengersFromDetails({ additionalDetails: bookingData.additionalDetails });
      const existingParentIds = new Set(existingInfants.map(({ passengerRef }) => passengerRef));

      const infantsToAdd = infants
        .map((i) => ({ ...i, firstName: i.firstName.trim(), lastName: i.lastName.trim() }))
        .filter(({ parentPassengerId }) => !existingParentIds.has(parentPassengerId));

      if (infantsToAdd.length > 0) {
        const results = await Promise.allSettled(
          infantsToAdd.map((infant) =>
            addInfant({
              variables: {
                bookingReference: bookingData.reference,
                passengerId: infant.parentPassengerId,
                firstName: infant.firstName,
                lastName: infant.lastName
              }
            })
          )
        );

        if (results.some((r) => r.status === 'rejected')) {
          setInfantAddError(true);
          return;
        }
      }
    }

    if (hasNothingDue) {
      await confirmBooking({
        variables: { bookingData, membershipNumber, sendConfirmationEmail: !bookingData.isGroupBooking }
      });
    }

    return history.push(pathname, history.location.state);
  };

  const updateMutation = hasFakeCustomer ? UPDATE_CUSTOMER_PASSENGERS : UPDATE_PASSENGERS;
  const mutation = hasCustomerDetails ? updateMutation : ADD_CUSTOMER_PASSENGERS;

  const passengerDetailsData = client.readQuery({
    query: gql`
      query {
        passengerDetails @client
      }
    `
  });
  const passengerDetails = passengerDetailsData?.passengerDetails || [];

  const [updatePassengers, { error: updatePassengersError, loading }] = useMutation(mutation);

  useEffect(() => {
    if (membershipNumber) {
      getCustomerInfo({
        variables: {
          membershipNumber
        }
      });
    }
  }, [getCustomerInfo, membershipNumber]);

  const passengersFromBookingOrCache =
    passengerDetails?.length > 0 ? passengerDetails.map(renameKeys({ paxId: 'id' })) : formatPassengers(bookingData.passengers);

  const firstPax = passengersFromBookingOrCache[0];
  if (!firstPax.firstName && customerInfo) {
    firstPax.firstName = customerInfo.firstName;
    firstPax.lastName = customerInfo.lastName;
    firstPax.email = customerInfo.emailAddress;
    firstPax.loyaltyNumber = customerInfo.profile?.loyalty?.membershipNumber?.substring(9);
    firstPax.birthDate = customerInfo.profile?.dateOfBirth;
    if (customerInfo.phoneNumber) {
      const parsedPhone = parsePhoneNumber(customerInfo.phoneNumber);

      firstPax.phoneCountryCode = parsedPhone.countryCode.replace('+', '');
      firstPax.phoneNumber = parsedPhone.number;
    }
  }

  const shouldWaitForLoyaltyCheck = membershipNumber && !customerInfoCalled;

  const handleIsAssistanceSelected = () => setIsAssistanceSelected(!isAssistanceSelected);

  const error =
    updatePassengersError ||
    confirmBookingError ||
    (infantAddError ? { message: 'Failed to add infant passengers', graphQLErrors: [] } : null);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/booking/${bookingData.reference}/pax${membershipNumberUrl}`
    });
  }

  const existingInfants =
    infantsOnLapEnabled && infantCount > 0 ? getInfantPassengersFromDetails({ additionalDetails: bookingData.additionalDetails }) : [];

  const addedParentIds = new Set(existingInfants.map(({ passengerRef }) => passengerRef));

  if (shouldWaitForLoyaltyCheck || customerInfoLoading) {
    return (
      <Column alignItems="center" justifyContent="center">
        <Spinner />
      </Column>
    );
  }

  return (
    <BookingPathPassengerDetails
      {...props}
      bookingData={bookingData}
      passengerDetails={passengersFromBookingOrCache}
      client={client}
      history={history}
      loading={loading}
      updatePassengers={updatePassengers}
      customerInfo={customerInfo}
      handleCompletedMutation={handleCompletedMutation}
      isAssistanceSelected={isAssistanceSelected}
      canAddSpecialAssistanceToBooking={canAddSpecialAssistanceToBooking}
      onIsAssistanceSelected={handleIsAssistanceSelected}
      infantsOnLapEnabled={infantsOnLapEnabled}
      infantCount={infantCount}
      addedParentIds={addedParentIds}
      existingInfants={existingInfants}
    />
  );
};

BookingPathPassengerDetailsQuery.propTypes = {
  bookingData: bookingShape,
  canAddSpecialAssistanceToBooking: PropTypes.bool.isRequired
};

export default BookingPathPassengerDetailsQuery;
